// v1 placement-redesign: when the AI/regex hybrid returns zero fields
// (blank form, novel layout, etc.), drop a sane default set of markers
// on the LAST page so the user has something to start from. They can
// drag, delete, or add as needed — the cost of being wrong is just a
// few seconds of fixup.
//
// Layout: each signer gets a (signature, date) pair stacked vertically
// in the lower-left quadrant. Multiple signers stagger downward.
import { signingFieldSizeForKind, type SignatureField } from '@corner/shared';
import type { ViewerSigner } from '../components/pdfViewer/signingTypes';

const SIGNATURE_DEFAULTS = signingFieldSizeForKind('signature');
const DATE_DEFAULTS = signingFieldSizeForKind('date');

/** Vertical band per signer, in percent. Signers stack from `Y_START`
 *  downward at `STEP_Y` increments. We cap so a signer never lands
 *  past `Y_MAX` — overflow signers just don't get default markers
 *  (rare: ≤5 signers fits comfortably). */
const Y_START = 60;
const STEP_Y = 14;
const Y_MAX = 90;

/** Horizontal slot for the signature column, then the date column. */
const X_SIGNATURE = 12;
const X_DATE = 36;

function newId(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return (crypto as Crypto).randomUUID();
  }
  return `field-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function generateDefaultMarkers(
  signers: ViewerSigner[],
  lastPageNumber: number,
): SignatureField[] {
  if (!signers.length || lastPageNumber <= 0) return [];

  const markers: SignatureField[] = [];
  signers.forEach((signer, idx) => {
    const yBase = Y_START + idx * STEP_Y;
    if (yBase + SIGNATURE_DEFAULTS.height > Y_MAX) return; // out of room

    const forRecipient = signer.role === 'other';
    markers.push({
      id: newId(),
      type: 'signature',
      page: lastPageNumber,
      x: X_SIGNATURE,
      y: yBase,
      width: SIGNATURE_DEFAULTS.width,
      height: SIGNATURE_DEFAULTS.height,
      label: `${signer.name || signer.role || 'Signer'} signature`,
      role: `${signer.role}_signature`,
      signedByEmail: signer.email,
      party: forRecipient ? 'other' : 'self',
      signer: forRecipient ? 'other' : 'you',
      assignedSignerIndex: idx,
      state: 'suggested',
    });
    markers.push({
      id: newId(),
      type: 'date',
      page: lastPageNumber,
      x: X_DATE,
      y: yBase,
      width: DATE_DEFAULTS.width,
      height: DATE_DEFAULTS.height,
      label: `${signer.name || signer.role || 'Signer'} date`,
      role: `${signer.role}_date`,
      signedByEmail: signer.email,
      party: forRecipient ? 'other' : 'self',
      signer: forRecipient ? 'other' : 'you',
      assignedSignerIndex: idx,
      state: 'suggested',
    });
  });

  return markers;
}
